import { ColumnDef } from '@tanstack/react-table';
import {
  SourcingFunnelDetails,
  SourcingMetricsFilterRange,
} from 'api-clients/monolith';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { DEFAULT_METRICS_FILTER_RANGE } from '@/hooks/use-metrics-filter-params';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

export function useSourcingOpeningsColumns(
  defaultMetricsPeriod?: SourcingMetricsFilterRange | null,
): ColumnDef<SourcingFunnelDetails>[] {
  const intl = useIntl();
  const { dashboardPath } = useNavigationPaths();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrencySymbol();

  const handleOpeningClick = (rowItem: SourcingFunnelDetails) => {
    if (!rowItem.active_hiring_goal) {
      const queryParams = new URLSearchParams();
      queryParams.set(
        'filter_range',
        defaultMetricsPeriod ?? DEFAULT_METRICS_FILTER_RANGE,
      );
      return navigate(
        `${dashboardPath}/${rowItem.slug}/channels?${queryParams.toString()}`,
      );
    }
    return navigate(`${dashboardPath}/${rowItem.slug}/channels`);
  };

  return [
    {
      accessorKey: 'name',
      cell: ({ row }) => {
        return (
          <div className="w-[220px] overflow-hidden">
            {row.original.name === 'Sourcing Openings' ? (
              <Typography variant="h6">
                {intl.formatMessage({
                  defaultMessage: 'Sourcing Openings',
                  description:
                    'Section title for the aggregate row in the openings table',
                })}
              </Typography>
            ) : (
              <Button
                variant="link"
                onClick={() => handleOpeningClick(row.original)}
                className="text-h6 text-fx-primary h-auto text-left font-medium whitespace-break-spaces"
              >
                {row.original.name}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'spend',
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Spend',
              description: 'Sourcing table: budget spent column label',
            })}
          </Typography>
          <Typography variant="large2" className="text-fx-neutral-600">
            {formatCurrency(row.original.spend ?? 0) ?? '-'}
          </Typography>
        </>
      ),
    },
    {
      accessorKey: 'duration',
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Duration (days)',
              description:
                'Sourcing table: how long the spend period runs column label',
            })}
          </Typography>
          <Typography variant="large2" className="text-fx-neutral-600">
            {row.original.duration ?? '-'}
          </Typography>
        </>
      ),
    },
    {
      accessorKey: 'applied_count',
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Sourcing table: count of people who applied column label',
            })}
          </Typography>
          <Typography variant="large2" className="text-fx-neutral-600">
            {row.original.applied_count ?? '-'}
          </Typography>
        </>
      ),
    },
    {
      accessorKey: 'cpa',
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'CPA',
              description:
                'Sourcing table: cost per applicant acronym column label',
            })}
          </Typography>
          <Typography variant="large2" className="text-fx-neutral-600">
            {formatCurrency(row.original.cpa ?? 0) ?? '-'}
          </Typography>
        </>
      ),
    },
    {
      accessorKey: 'conversion',
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Conversion',
              description:
                'Sourcing table: conversion rate as a percent column label',
            })}
          </Typography>
          <Typography variant="large2" className="text-fx-neutral-600">
            {row.original.conversion ? `${row.original.conversion}%` : '-'}
          </Typography>
        </>
      ),
    },
    {
      accessorKey: 'hired_count',
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Sourcing table: people hired in the period column label',
            })}
          </Typography>
          <Typography variant="large2" className="text-fx-neutral-600">
            {row.original.hired_count ?? '-'}
          </Typography>
        </>
      ),
    },
  ];
}
